import { css, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import type {
  HelpDetail,
  ScheduleAppointmentsContent,
  AppointmentInfo,
} from "../../types/content";
import "../../elements/web-kiosk-home-link";
import { WebKioskPageElement } from "../web-kiosk-page";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-schedule-appointments": WebKioskScheduleAppointmentsElement;
  }
}

@customElement("web-kiosk-schedule-appointments")
export class WebKioskScheduleAppointmentsElement extends WebKioskPageElement {
  @state() content: ScheduleAppointmentsContent | undefined = undefined;

  renderHelpDetails(
    details: HelpDetail[] | undefined
  ): TemplateResult[] | typeof nothing {
    if (!details || details.length === 0) {
      return nothing;
    }

    return details.map(
      (detail) =>
        html`<li>
          <div class="detail-icon">
            <img src=${detail.icon} alt=${detail.title} />
          </div>
          <div class="detail-text">
            <h3>${detail.title}</h3>
            <div class="detail-description">
              ${unsafeHTML(detail.description)}
            </div>
          </div>
        </li>`
    );
  }

  renderAppointmentInfo(
    info: AppointmentInfo | undefined
  ): TemplateResult | typeof nothing {
    if (!info) {
      return nothing;
    }

    return html`<div id="appointment">
      <dt><strong>${info.text}</strong></dt>
      <dd>${info.number}</dd>
    </div>`;
  }

  render(): TemplateResult {
    return html`<div class="page-body">
      <section id="info">
        <web-kiosk-home-link
          link=${this.createRelativeLink("home")}
        ></web-kiosk-home-link>
        <h1>${this.content?.infoHeader}</h1>
        <p>${this.content?.infoDescription}</p>
      </section>
      <section id="details">
        <div class="details-inner">
          <h2>${this.content?.detailsHeader}</h2>
          <ul id="help-details">
            ${this.renderHelpDetails(this.content?.details)}
          </ul>
          <dl id="extra-info">
            ${this.renderAppointmentInfo(this.content?.appointment)}
          </dl>
        </div>
      </section>
    </div>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c2e8ff;
      --web-kiosk-text-color: #323264;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-details-bg-color: #f5f5f0;
      --web-kiosk-details-description-color: #0a1414;
      --web-kiosk-details-description-heading-border-color: #32326429;
      --web-kiosk-dimension-spacer-xxs: 8px;
      --web-kiosk-dimension-spacer-m: 32px;
      --web-kiosk-dimension-spacer-l: 64px;
    }

    .page-body {
      display: flex;
      height: 100%;
      background: url("assets/scheduleAppointments/bg/info.svg") 0 100%
          no-repeat,
        url("assets/scheduleAppointments/bg/details.svg") 100% 0 no-repeat,
        linear-gradient(
          90deg,
          var(--web-kiosk-bg, hotpink) 35%,
          var(--web-kiosk-details-bg-color, hotpink) 0 100%
        );
      background-size: 35%, 58%, 100%;
    }

    web-kiosk-home-link {
      padding: 48px 56px;
    }

    #info {
      width: 35%;
    }

    #info h1 {
      margin: 0;
      padding: 4px var(--web-kiosk-dimension-spacer-m, 32px) 4px
        var(--web-kiosk-dimension-spacer-l, 64px);
      font-size: var(--web-kiosk-dimension-spacer-l, 64px);
      font-weight: 700;
      line-height: 72px;
      color: var(--web-kiosk-text-color, hotpink);
    }

    .detail-description p {
      margin: 0;
    }

    .detail-description p:empty {
      margin-top: 0.9em;
    }

    #info p {
      margin: 0;
      padding: 36px 48px 345px var(--web-kiosk-dimension-spacer-l, 64px);
      line-height: 1.71;
      font-size: 1.75rem;
      font-weight: 400;
      color: var(--web-kiosk-text-color, hotpink);
    }

    #details {
      width: 65%;
    }

    #details h2 {
      margin: 0;
      margin-bottom: 40px;
      padding-bottom: 40px;
      border-bottom: 1px solid
        var(--web-kiosk-details-description-heading-border-color, hotpink);
      font-size: 2rem;
      line-height: 1.16;
      font-weight: 600;
      color: var(--web-kiosk-text-color, hotpink);
    }

    .details-inner {
      padding: 188px 145px 0 120px;
    }

    #help-details {
      padding-left: 50px;
      margin-bottom: 0;
    }

    #help-details li {
      display: flex;
      gap: var(--web-kiosk-dimension-spacer-m, 32px);
    }

    #help-details li:not(:last-of-type) {
      margin-bottom: var(--web-kiosk-dimension-spacer-m, 32px);
    }

    .detail-text h3 {
      margin: 0 0 0.31em;
      font-size: 1.625rem;
      font-weight: 700;
      line-height: 1.35;
      color: var(--web-kiosk-text-color, hotpink);
    }

    .detail-description {
      font-size: 1.375rem;
      line-height: 1.45;
      color: var(--web-kiosk-details-description-color, hotpink);
    }

    #extra-info {
      display: flex;
      justify-content: flex-end;
      min-height: 89px;
      align-items: center;
    }

    :is(#appointment) {
      display: flex;
      align-items: center;
    }

    :is(#appointment) dt {
      font-size: 1.5rem;
      line-height: 1.25;
      margin-right: 0.33em;
    }

    :is(#appointment) dt strong {
      font-weight: 600;
    }

    :is(#appointment) dd {
      margin: 0;
      font-size: 1.5rem;
      line-height: 1.25;
    }
  `;
}
